import { Card } from "@/components/ui/card";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import { ChartCard, cr2, r2 } from "./MISOverview";

const tooltipStyle = { fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" };
const REASON_COLORS = ["#dc2626", "#f59e0b", "#0ea5e9", "#64748b", "#a855f7"];

const normReason = (r) => {
  const s = (r || "").toLowerCase();
  if (s.includes("pric")) return "Pricing";
  if (s.includes("pref")) return "Preference";
  if (s.includes("late")) return "Late reach";
  if (s.includes("pay")) return "Payment terms";
  return r ? "Other" : "Not stated";
};

export function MISInsights({ losses, dcrTeams, dcrWeeks, bd, dealers, dealerPlans, dealerFunnel }) {
  const lostByComp = Object.entries(losses.reduce((acc, w) => {
    const k = w.lost_to || "Unknown";
    acc[k] = (acc[k] || 0) + w.value_lakhs;
    return acc;
  }, {})).map(([name, v]) => ({ name, value: r2(v / 100) })).sort((a, b) => b.value - a.value).slice(0, 8);

  const reasons = Object.entries(losses.reduce((acc, w) => {
    const k = normReason(w.reason);
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {})).map(([name, value]) => ({ name, value }));

  const dcrSeries = dcrWeeks.map((w) => {
    const row = { week: w.replace(/\s+/g, " ").slice(0, 14) };
    let calls = 0, enqs = 0;
    dcrTeams.forEach((t) => {
      const s = t.series.find((x) => x.week === w);
      if (s) { calls += s.calls; enqs += s.enqs; }
    });
    row.Calls = calls;
    row.Enquiries = enqs;
    return row;
  });
  const totCalls = dcrSeries.reduce((a, r) => a + r.Calls, 0);
  const totEnqs = dcrSeries.reduce((a, r) => a + r.Enquiries, 0);

  const bdActive = bd.filter((b) => b.status === "Active");
  const bdByBranch = Object.entries(bdActive.reduce((acc, b) => {
    const k = b.branch || "—";
    acc[k] = (acc[k] || 0) + b.value_lakhs;
    return acc;
  }, {})).map(([name, v]) => ({ name, value: r2(v / 100) })).sort((a, b) => b.value - a.value).slice(0, 8);

  const topDealerPlans = [...dealerPlans].sort((a, b) => b.plan - a.plan).slice(0, 10)
    .map((p) => ({ name: p.dealer.slice(0, 14), Plan: r2(p.plan), Actual: r2(p.actual) }));
  const dealerTargets = dealers.reduce((a, m) => a + m.target_cr, 0);
  const dealerPipeline = dealerFunnel.reduce((a, d) => a + d.value_lakhs, 0) / 100;

  return (
    <>
      <div className="grid lg:grid-cols-3 gap-5 mb-5">
        <ChartCard title="Lost value by competitor (INR Cr)" subtitle={`${losses.length} lost projects · ${cr2(losses.reduce((a, w) => a + w.value_lakhs, 0) / 100)}`} testId="mis-chart-competitors" height={250}>
          <ResponsiveContainer>
            <BarChart data={lostByComp} layout="vertical" margin={{ top: 0, right: 12, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={78} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" fill="#dc2626" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Why we lose" subtitle="Loss reasons from Win-Loss sheet" testId="mis-chart-reasons" height={250}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={reasons} dataKey="value" nameKey="name" innerRadius={48} outerRadius={82} paddingAngle={3}>
                {reasons.map((_, i) => <Cell key={i} fill={REASON_COLORS[i % REASON_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Call activity (DCR)" subtitle={`${totCalls} calls · ${totEnqs} enquiries · ${totCalls ? Math.round((totEnqs / totCalls) * 100) : 0}% conversion`} testId="mis-chart-dcr" height={250}>
          <ResponsiveContainer>
            <LineChart data={dcrSeries} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="week" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="Calls" stroke="#0ea5e9" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Enquiries" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mb-5">
        <ChartCard title="BD pipeline by branch (INR Cr)" subtitle={`${bdActive.length} active BD leads`} testId="mis-chart-bd" height={250}>
          <ResponsiveContainer>
            <BarChart data={bdByBranch} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" fill="#a855f7" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <div className="lg:col-span-2">
          <ChartCard title="Dealer plan vs actual (INR Cr)" subtitle={`${dealers.length} dealers onboarded · ${cr2(dealerTargets)} targets · dealer-route pipeline ${cr2(dealerPipeline)}`} testId="mis-chart-dealers" height={250}>
            <ResponsiveContainer>
              <BarChart data={topDealerPlans} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-24} textAnchor="end" height={48} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Plan" fill="#94a3b8" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Actual" fill="#10b981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </>
  );
}
